import { Link } from "@tanstack/react-router";
import { ChevronDown, LogOut, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Footer } from "@/components/brand/Footer";
import { HatchBand, Version, cn, useTheme } from "@jbnl/shared-ui";
import { Logo } from "@/components/brand/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSignOut } from "@/features/session/useSignOut";

/**
 * The letterhead rather than an application frame. `wide` is for the one two-pane screen; the
 * rest keep the narrow column, because this site is a form and a full-width form is harder.
 */
export function Shell({
  name,
  wide,
  children,
}: {
  name?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  // Signed out the header carries nothing but the lockup, so it stands as the letterhead
  // proper - the same frame the backoffice signs you in on, over one narrow column.
  if (!name) {
    return (
      <div className="relative flex min-h-screen flex-col bg-background">
        <HatchBand />
        <div className="mx-auto w-full max-w-2xl px-4 py-12 sm:px-6">
          {/* The rule under the lockup is the letterhead's, so it stays on the wrapper -
              the link is the mark and the words, not the line beneath them. */}
          <div className="mb-8 border-b border-brand-blue pb-4">
            <Merk logo="h-14" />
          </div>
          {children}
          {/* Under the page rather than in the header: on this frame the lockup is the whole
              chrome, and the build is a footnote, not a second thing to read at the top. */}
          <Version className="mt-10" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-sunken">
      <HatchBand />
      {/* A pseudo-element carries the header's surface up a viewport: overscroll would otherwise
          open a gap the viewport-fixed band shows through. */}
      <header
        className={cn(
          "sticky top-0 z-10 border-b border-brand-blue bg-background",
          "before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full",
          "before:h-screen before:bg-background",
        )}
      >
        <div
          className={cn(
            "mx-auto flex items-center justify-between gap-4 py-3",
            kolom(wide),
          )}
        >
          <Merk logo="h-11" gap="gap-3" />
          <SessieMenu name={name} />
        </div>
      </header>
      <main className={cn("mx-auto w-full py-8", kolom(wide))}>{children}</main>
      <Footer />
    </div>
  );
}

/**
 * The narrow column never reaches the band, but `max-w-6xl` only clears it past 1256px - so the
 * wide column pays for the band out of its own right padding, keeping the margins even.
 */
const kolom = (wide?: boolean) =>
  wide ? "max-w-6xl px-4 sm:px-6 lg:pr-[76px]" : "max-w-4xl px-4 sm:px-6";

/**
 * The whole lockup is the target, not the mark alone: a 44px logo is small to aim at. `/` sorts
 * out where that lands - the menu, or their one screen for everybody else.
 */
function Merk({ logo, gap = "gap-4" }: { logo: string; gap?: string }) {
  const { t } = useTranslation();

  return (
    <Link to="/" className={cn("flex items-center rounded-sm", gap)}>
      <Logo className={logo} />
      <span className="font-bold tracking-[-0.02em]">{t("shell.title")}</span>
    </Link>
  );
}

/** The name in the header is the only handle on the session, so it is what opens the menu. */
function SessieMenu({ name }: { name: string }) {
  const { t } = useTranslation();
  const signOut = useSignOut();
  const theme = useTheme();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="shrink-0 whitespace-nowrap text-muted-foreground">
        {name}
        <ChevronDown />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {/* The only way in: nothing else on this site links to the account page, and the name in
            the header is where somebody looks for their own settings. */}
        <DropdownMenuItem asChild>
          <Link to="/account">
            <UserRound />
            {t("shell.account")}
          </Link>
        </DropdownMenuItem>
        {/* Stays open, so the next press steps to the next choice. */}
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            theme.cycle();
          }}
        >
          <theme.Icon />
          {theme.label}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => signOut.mutate()}
          disabled={signOut.isPending}
        >
          <LogOut />
          {signOut.isPending ? t("actions.busy") : t("shell.signOut")}
        </DropdownMenuItem>
        {/* Not an item: there is nothing to choose here, and a focusable row that does nothing
            is a row the keyboard has to be walked past. */}
        <Version className="border-t px-2 pt-2 pb-1" />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
